// src/utils/serverConfig.ts

export interface ServerUrls {
  API_BASE_URL: string | null
  WS_BASE_URL: string | null
}

export const SERVER_PORT = 57443

const DEV_SERVER_PORT = String(import.meta.env.VITE_DEV_SERVER_PORT ?? import.meta.env.REACT_APP_DEV_SERVER_PORT ?? 3000)

function isLocalDevWebOrigin(locationLike: Location): boolean {
  const hostname = locationLike.hostname
  const port = locationLike.port

  return hostname === "localhost" || hostname === "127.0.0.1" || port === DEV_SERVER_PORT
}

function getWebOriginUrls(): ServerUrls | null {
  if (typeof window === "undefined") return null
  if (__IS_CAPACITOR__ || window.electronAPI) return null

  if (isLocalDevWebOrigin(window.location)) {
    return null
  }

  const origin = window.location.origin
  if (!origin || !/^https?:/i.test(origin)) {
    return null
  }

  return {
    API_BASE_URL: origin,
    WS_BASE_URL: origin.replace(/^http/i, "ws"),
  }
}

/**
 * 크롬 브라우저 개발 모드 감지
 * VITE_DEV_SERVER_IP가 설정되면 Vite 프록시(localhost)를 경유하여
 * self-signed 인증서 문제를 우회한다.
 */
function getDevProxyUrls(): ServerUrls | null {
  if (typeof window === "undefined") return null
  if (__IS_CAPACITOR__) return null
  if (window.electronAPI) return null

  const devIp = import.meta.env.VITE_DEV_SERVER_IP
  if (!devIp) return null

  // Vite 프록시를 통해 http/ws로 접속 (프록시가 https/wss로 중계)
  const origin = window.location.origin // http://localhost:3000
  if (!origin || !/^https?:/i.test(origin)) {
    return null
  }

  return {
    API_BASE_URL: origin,
    WS_BASE_URL: origin.replace(/^http/, "ws"),
  }
}

// localStorage 값이 설정될 때까지 기다리는 함수
export function waitForServerConfig(maxAttempts = 10, interval = 500): Promise<ServerUrls> {
  return new Promise(resolve => {
    // 크롬 브라우저 개발 모드: Vite 프록시 경유
    const devProxy = getDevProxyUrls()
    if (devProxy) {
      resolve(devProxy)
      return
    }

    const webOriginUrls = getWebOriginUrls()
    if (webOriginUrls) {
      resolve(webOriginUrls)
      return
    }

    let attempts = 0

    const checkConfig = () => {
      attempts++
      const serverHost = localStorage.getItem("SigmaServerIp")

      if (serverHost && serverHost !== "") {
        // 서버 IP가 설정된 경우
        resolve({
          API_BASE_URL: `https://${serverHost}:${SERVER_PORT}`,
          WS_BASE_URL: `wss://${serverHost}:${SERVER_PORT}`,
        })
        return
      }

      if (attempts >= maxAttempts) {
        // 최대 시도 횟수에 도달한 경우 null 반환
        console.warn(`서버 설정을 ${maxAttempts}회 시도했지만 찾을 수 없습니다.`)
        resolve({
          API_BASE_URL: null,
          WS_BASE_URL: null,
        })
        return
      }

      // 다시 시도
      setTimeout(checkConfig, interval)
    }

    checkConfig()
  })
}

// 현재 서버 URL을 가져오는 함수
export function getCurrentServerUrls(): ServerUrls {
  // 크롬 브라우저 개발 모드: Vite 프록시 경유
  const devProxy = getDevProxyUrls()
  if (devProxy) return devProxy

  const webOriginUrls = getWebOriginUrls()
  if (webOriginUrls) return webOriginUrls

  const serverHost = localStorage.getItem("SigmaServerIp")

  if (!serverHost || serverHost === "") {
    return {
      API_BASE_URL: null,
      WS_BASE_URL: null,
    }
  }

  return {
    API_BASE_URL: `https://${serverHost}:${SERVER_PORT}`,
    WS_BASE_URL: `wss://${serverHost}:${SERVER_PORT}`,
  }
}

/**
 * 서버 health check — /health/ 엔드포인트로 실제 서버 응답 확인
 * @param timeoutMs 타임아웃(밀리초), 기본 5000ms
 * @returns 서버가 정상 응답하면 true, 그렇지 않으면 false
 */
export async function checkServerHealth(timeoutMs = 5000): Promise<boolean> {
  const urls = getCurrentServerUrls()
  if (!urls.API_BASE_URL) return false

  try {
    const res = await fetch(`${urls.API_BASE_URL}/health/`, {
      signal: AbortSignal.timeout(timeoutMs),
    })
    return res.ok
  } catch {
    return false
  }
}

// localStorage 변경을 감지하는 이벤트 리스너
export function onServerConfigChange(callback: (urls: ServerUrls) => void): () => void {
  const handleStorageChange = (event: StorageEvent) => {
    if (event.key === "SigmaServerIp") {
      const urls = getCurrentServerUrls()
      callback(urls)
    }
  }

  window.addEventListener("storage", handleStorageChange)

  // cleanup 함수 반환
  return () => {
    window.removeEventListener("storage", handleStorageChange)
  }
}

